import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash2, Save, X, BookMarked, Search, ArrowLeft, Eye, Calendar, Users, FileText, Loader, ChevronLeft, ChevronRight, PlusCircle } from 'lucide-react';
import axios from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/toast';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import Unauthorized from './UnauthorizedAccess';

const ManageSpecialIssues = () => {
  const { user } = useAuth();
  const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);
  
  const [specialIssues, setSpecialIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Publications drill-down state
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [publications, setPublications] = useState([]);
  const [pubLoading, setPubLoading] = useState(false);

  // Bulk remove state
  const [selectedPubIds, setSelectedPubIds] = useState(new Set());
  const [selectAllPubs, setSelectAllPubs] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Add-publications modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableJournals, setAvailableJournals] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [availPagination, setAvailPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [availSearch, setAvailSearch] = useState('');
  const [availSearchInput, setAvailSearchInput] = useState('');
  const [selectedJournalIds, setSelectedJournalIds] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const availLoadingRef = useRef(false);
  const availPaginationRef = useRef(availPagination);
  availPaginationRef.current = availPagination;
  availLoadingRef.current = availLoading;

  const fetchSpecialIssues = async () => {
    try {
      const response = await axios.get('/publications/special-issues/all');
      if (response.data.status === 'success') {
        setSpecialIssues(response.data.specialIssues);
      }
    } catch (err) {
      showToast('Failed to load special issues', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSpecialIssues(); }, []);

  const showMessage = (type, text) => {
    showToast(text, type);
  };

  const fetchPublications = async (issue) => {
    setSelectedIssue(issue);
    setPubLoading(true);
    setPublications([]);
    setSelectedPubIds(new Set());
    setSelectAllPubs(false);
    try {
      const response = await axios.get(`/publications/special-issues/${issue.special_issue_id}/journals`);
      if (response.data.status === 'success') {
        setPublications(response.data.journals);
      }
    } catch (err) {
      showMessage('error', 'Failed to load publications');
    } finally {
      setPubLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedIssue(null);
    setPublications([]);
  };

  // Fetch available journals for the add modal
  const fetchAvailableJournals = useCallback(async (page, limit, search, excludeIds, mode = 'replace') => {
    setAvailLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (excludeIds.length > 0) params.exclude = excludeIds;
      const response = await axios.get('/publications/special-issues/available-journals', { params });
      if (response.data.status === 'success') {
        setAvailableJournals(prev => mode === 'append' ? [...prev, ...response.data.journals] : response.data.journals);
        setAvailPagination(response.data.pagination);
      }
    } catch (err) {
      showMessage('error', 'Failed to load journals');
    } finally {
      setAvailLoading(false);
    }
  }, []);

  const openAddModal = () => {
    setShowAddModal(true);
    setAvailSearchInput('');
    setAvailSearch('');
    setSelectedJournalIds(new Set());
    setSelectAll(false);
    const excludeIds = publications.map(p => p.id);
    fetchAvailableJournals(1, availPagination.limit, '', excludeIds);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAvailableJournals([]);
  };

  const handleAvailSearch = () => {
    setAvailSearch(availSearchInput);
    const excludeIds = publications.map(p => p.id);
    fetchAvailableJournals(1, availPagination.limit, availSearchInput, excludeIds);
  };

  const handleAvailLimitChange = (newLimit) => {
    const excludeIds = publications.map(p => p.id);
    fetchAvailableJournals(1, parseInt(newLimit), availSearch, excludeIds);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedJournalIds(new Set(availableJournals.map(j => j.buffer)));
    } else {
      setSelectedJournalIds(new Set());
    }
  };

  const handleToggleJournal = (buffer) => {
    setSelectedJournalIds(prev => {
      const next = new Set(prev);
      if (next.has(buffer)) {
        next.delete(buffer);
      } else {
        next.add(buffer);
      }
      setSelectAll(next.size === availableJournals.length && availableJournals.length > 0);
      return next;
    });
  };

  const handleAssignBulk = async () => {
    if (selectedJournalIds.size === 0) return;
    setAssigning(true);
    try {
      const response = await axios.put(`/publications/special-issues/${selectedIssue.special_issue_id}/assign-bulk`, {
        journalIds: Array.from(selectedJournalIds)
      });
      if (response.data.status === 'success') {
        showMessage('success', `${response.data.assignedCount} publication(s) assigned`);
        closeAddModal();
        fetchPublications(selectedIssue);
        fetchSpecialIssues();
      } else {
        showMessage('error', response.data.error || 'Failed to assign publications');
      }
    } catch (err) {
      showMessage('error', 'Failed to assign publications');
    } finally {
      setAssigning(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const response = await axios.post('/publications/special-issues/create', { special_issue_name: newName.trim() });
      if (response.data.status === 'success') {
        showMessage('success', 'Special issue created successfully');
        setNewName('');
        setShowCreateForm(false);
        fetchSpecialIssues();
      }
    } catch (err) {
      showMessage('error', 'Failed to create special issue');
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      const response = await axios.put(`/publications/special-issues/update/${id}`, { special_issue_name: editName.trim() });
      if (response.data.status === 'success') {
        showMessage('success', 'Special issue updated successfully');
        setEditingId(null);
        setEditName('');
        fetchSpecialIssues();
      }
    } catch (err) {
      showMessage('error', 'Failed to update special issue');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this special issue? Journals linked to it will be unlinked.')) return;
    try {
      const response = await axios.delete(`/publications/special-issues/delete/${id}`);
      if (response.data.status === 'success') {
        showMessage('success', 'Special issue deleted successfully');
        fetchSpecialIssues();
        if (selectedIssue?.special_issue_id === id) handleBack();
      }
    } catch (err) {
      showMessage('error', 'Failed to delete special issue');
    }
  };

  const handleTogglePub = (id) => {
    setSelectedPubIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelectAllPubs(next.size === publications.length && publications.length > 0);
      return next;
    });
  };

  const handleSelectAllPubs = (checked) => {
    setSelectAllPubs(checked);
    setSelectedPubIds(checked ? new Set(publications.map(p => p.id)) : new Set());
  };

  const handleBulkRemove = async () => {
    if (selectedPubIds.size === 0) return;
    if (!window.confirm(`Remove ${selectedPubIds.size} publication(s) from this special issue?`)) return;
    setRemoving(true);
    try {
      const response = await axios.put(`/publications/special-issues/${selectedIssue.special_issue_id}/remove-bulk`, {
        journalIds: Array.from(selectedPubIds)
      });
      if (response.data.status === 'success') {
        showMessage('success', `${response.data.removedCount} publication(s) removed`);
        setSelectedPubIds(new Set());
        setSelectAllPubs(false);
        fetchPublications(selectedIssue);
        fetchSpecialIssues();
      } else {
        showMessage('error', response.data.error || 'Failed to remove publications');
      }
    } catch (err) {
      showMessage('error', 'Failed to remove publications');
    } finally {
      setRemoving(false);
    }
  };

  const filteredIssues = specialIssues.filter(si =>
    si.special_issue_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAuthorized) return <Unauthorized />;

  return (
    <div className="p-6">
      {selectedIssue ? (
        <>
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={handleBack}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{selectedIssue.special_issue_name}</h1>
              <p className="text-sm text-gray-500 mt-1">
                {publications.length} publication{publications.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={openAddModal}
              className="ml-auto inline-flex items-center px-3 py-1.5 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition-colors text-xs font-medium"
            >
              <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
              Add Publications
            </button>
          </div>

          {pubLoading ? (
            <div className="text-center py-12 text-gray-500">Loading publications...</div>
          ) : publications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="mb-4">No publications linked to this special issue</p>
              <button
                onClick={openAddModal}
                className="inline-flex items-center px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition-colors text-sm font-medium"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Add publications to special issue
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-2 py-3">
                      <input
                        type="checkbox"
                        checked={selectAllPubs}
                        onChange={(e) => handleSelectAllPubs(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-purple-700 focus:ring-purple-500"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Title</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Authors</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Date Published</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Views</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Downloads</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {publications.map((pub) => (
                    <tr key={pub.id} className={`hover:bg-gray-50 ${selectedPubIds.has(pub.id) ? 'bg-purple-50' : ''}`}>
                      <td className="w-10 px-2 py-3">
                        <input
                          type="checkbox"
                          checked={selectedPubIds.has(pub.id)}
                          onChange={() => handleTogglePub(pub.id)}
                          className="w-4 h-4 rounded border-gray-300 text-purple-700 focus:ring-purple-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-800">{pub.title}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {pub.authors?.length > 0 ? pub.authors.join(', ') : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            {pub.date_published
                              ? new Date(pub.date_published).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                              : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-gray-600">{pub.views_count || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-gray-600">{pub.downloads_count || 0}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {selectedPubIds.size > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm text-gray-600">{selectedPubIds.size} selected</span>
                  <button
                    onClick={handleBulkRemove}
                    disabled={removing}
                    className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {removing ? (
                      <><Loader className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Removing...</>
                    ) : (
                      <><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove Selected</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Add Publications Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-12 px-4" onClick={closeAddModal}>
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">Add Publications</h2>
                    <p className="text-sm text-gray-500">Assign publications to <strong>{selectedIssue?.special_issue_name}</strong></p>
                  </div>
                  <button onClick={closeAddModal} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Search & Limit Controls */}
                <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={availSearchInput}
                      onChange={(e) => setAvailSearchInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAvailSearch()}
                      placeholder="Search by title or author..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <button onClick={handleAvailSearch} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">Search</button>
                  <select
                    value={availPagination.limit}
                    onChange={(e) => handleAvailLimitChange(e.target.value)}
                    className="px-1.5 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 w-24"
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                  </select>
                </div>

                {/* Results Count & Select All */}
                <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between text-sm text-gray-600">
                  <span>{availPagination.total} journal(s) found</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-700 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium">Select All</span>
                  </label>
                </div>

                {/* Journal List */}
                <div className="flex-1 overflow-y-auto px-6 py-2" onScroll={(e) => {
                  const el = e.currentTarget;
                  const hasMore = availPaginationRef.current.page < availPaginationRef.current.totalPages;
                  if (availLoadingRef.current || !hasMore) return;
                  if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
                    const excludeIds = publications.map(p => p.id);
                    fetchAvailableJournals(availPaginationRef.current.page + 1, availPaginationRef.current.limit, availSearch, excludeIds, 'append');
                  }
                }}>
                  {availableJournals.length === 0 && !availLoading ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No journals found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {availableJournals.map((journal) => (
                        <label
                          key={journal.buffer}
                          className={`flex items-start gap-3 py-3 px-2 rounded-lg cursor-pointer transition-colors ${
                            selectedJournalIds.has(journal.buffer) ? 'bg-purple-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedJournalIds.has(journal.buffer)}
                            onChange={() => handleToggleJournal(journal.buffer)}
                            className="mt-1 w-4 h-4 rounded border-gray-300 text-purple-700 focus:ring-purple-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 line-clamp-1">{journal.title}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {journal.authors?.length > 0 ? journal.authors.join(', ') : '-'}
                              </span>
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {journal.date_published
                                  ? new Date(journal.date_published).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                  : 'Unpublished'}
                              </span>
                            </div>
                          </div>
                        </label>
                      ))}
                      {availLoading && (
                        <div className="flex items-center justify-center py-6 text-gray-400">
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          <span className="text-sm">Loading more...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer: Assign Button */}
                <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm text-gray-500">{availPagination.total} journal(s) — showing {availableJournals.length}</span>
                  <button
                    onClick={handleAssignBulk}
                    disabled={selectedJournalIds.size === 0 || assigning}
                    className="inline-flex items-center px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {assigning ? (
                      <><Loader className="w-4 h-4 mr-2 animate-spin" /> Assigning...</>
                    ) : (
                      <><PlusCircle className="w-4 h-4 mr-2" /> Assign Selected ({selectedJournalIds.size})</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Manage Special Issues</h1>
              <p className="text-sm text-gray-500 mt-1">Click a special issue to view its publications</p>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Special Issue
            </button>
          </div>

          {showCreateForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">New Special Issue</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter special issue name..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
                <button onClick={handleCreate} className="px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-800 text-sm font-medium">Create</button>
                <button onClick={() => { setShowCreateForm(false); setNewName(''); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">Cancel</button>
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search special issues..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : filteredIssues.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BookMarked className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No special issues found</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Publications</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Date Created</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Last Modified</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredIssues.map((si) => (
                    <tr
                      key={si.special_issue_id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => fetchPublications(si)}
                    >
                      <td className="px-4 py-3">
                        {editingId === si.special_issue_id ? (
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              onKeyDown={(e) => e.key === 'Enter' && handleUpdate(si.special_issue_id)}
                              autoFocus
                            />
                            <button onClick={() => handleUpdate(si.special_issue_id)} className="p-1 text-green-600 hover:text-green-800">
                              <Save className="w-4 h-4" />
                            </button>
                            <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm font-medium text-purple-700 hover:text-purple-900">{si.special_issue_name}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          {si.publication_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(si.date_created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {si.last_modified ? new Date(si.last_modified).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => { setEditingId(si.special_issue_id); setEditName(si.special_issue_name); }}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(si.special_issue_id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageSpecialIssues;
